import * as Sentry from "@sentry/react";
import { isInternalUserId } from "@/lib/internalAccounts";

/**
 * Issue #642 — tell Sentry WHICH mom hit an error.
 *
 * Without this every Sentry issue reads "Users 0", so 26 events could be one
 * mom pull-to-refreshing 26 times or 26 different moms locked out of the app.
 * Launch-day triage (Aug 5, 2026) was guesswork for exactly this reason.
 *
 * ⚠️ ID ONLY. Never email, name, phone, or anything else off the Supabase
 * `user` object. `sentryUserFromId` takes a bare string precisely so a whole
 * `User` (which carries `email`, `phone` and `user_metadata`) can't be handed
 * to it by accident — the type won't allow it. Sentry's `User` shape has
 * optional `email` / `username` / `ip_address` fields that would happily
 * accept PII if someone widened this later; the payload test in
 * `src/lib/__tests__/sentryUser.test.ts` asserts the object has exactly one
 * key so that change fails loudly instead of quietly shipping emails to a
 * third party.
 *
 * The Supabase user id is already in Sentry-adjacent places (it's the
 * OneSignal external_id, and it's what every RLS policy keys on), so this adds
 * no new identifier — it just makes the existing one visible where triage
 * happens.
 */

/** Maps a Supabase user id to the ONLY user payload Sentry is allowed to see. */
export function sentryUserFromId(userId: string | null | undefined): { id: string } | null {
  return userId ? { id: userId } : null;
}

/**
 * Tags the event with whether the account is one of ours (#1476).
 *
 * ⚠️ A LABEL, NOT A FILTER. The event is still sent in full — triage excludes
 * internal noise with `!internal:true` in the Sentry UI. Dropping internal
 * events instead would silence the crashes of the one person guaranteed to be
 * on the newest build.
 *
 * `null` CLEARS the tag rather than writing "false", same as `syncSentryTier`.
 * A signed-out error on the login screen might well be Kasandra, so "false"
 * would be a claim about an identity we do not have — and `internal:false` is
 * the value ops queries trust to mean "real customer". Absent is the honest
 * third state; filtering with `!internal:true` keeps those events visible.
 */
export function syncSentryInternal(userId: string | null | undefined): void {
  Sentry.setTag(
    "internal",
    userId ? (isInternalUserId(userId) ? "true" : "false") : undefined,
  );
}

/**
 * Pushes the current identity (or nothing, on sign-out) onto Sentry's global scope.
 *
 * The `internal` tag is written HERE, from the same id, rather than by a caller
 * making a second call it could forget: an event carrying an internal uuid with
 * no `internal` tag is precisely the state that cost the Sep 6 triage session.
 * Tag first, then identity — nothing can fire between two synchronous calls
 * today, but the ordering means the tag can never lag the id it describes.
 */
export function syncSentryUser(userId: string | null | undefined): void {
  syncSentryInternal(userId);
  Sentry.setUser(sentryUserFromId(userId));
}

/** The only two values the `tier` tag is allowed to take. */
export type SentryTier = "free" | "pro";

/**
 * Tags the event with her plan so triage can split free from Pro (#870).
 *
 * Passing `null` clears the tag rather than writing "free" — a signed-out
 * session has no tier, and recording one would put every logged-out error in
 * the free bucket and skew exactly the comparison this tag exists to make.
 * Sentry's `Primitive` accepts `undefined` and treats it as removal.
 */
export function syncSentryTier(tier: SentryTier | null): void {
  Sentry.setTag("tier", tier ?? undefined);
}
